import { pipeline, env } from '@xenova/transformers'

env.remoteHost = import.meta.env.VITE_HF_ENDPOINT || 'https://hf-mirror.com'

interface ImageAnalysisResult {
  imageId: string
  task: string
  result: any
  confidence: number
  timestamp: number
}

interface ObjectDetectionResult {
  boxes: Array<{
    x1: number
    y1: number
    x2: number
    y2: number
    label: string
    score: number
  }>
}

interface ImageClassificationResult {
  labels: Array<{
    label: string
    score: number
  }>
}

interface ImageCaptionResult {
  caption: string
}

interface FaceDetectionResult {
  faces: Array<{
    x1: number
    y1: number
    x2: number
    y2: number
    confidence: number
  }>
}

export class AIImageAnalysis {
  private classifier: any = null
  private detector: any = null
  private captioner: any = null
  private faceDetector: any = null
  private isLoading = false
  private analysisHistory: Map<string, ImageAnalysisResult> = new Map()

  async initialize() {
    if (this.isLoading) {
      return
    }

    this.isLoading = true
    try {
      await this.loadModels()
    } finally {
      this.isLoading = false
    }
  }

  private async loadModels() {
    try {
      this.classifier = await pipeline('image-classification', 'Xenova/resnet-50')
    } catch (e) {
      console.warn('Failed to load image classification model:', e)
    }

    try {
      this.detector = await pipeline('object-detection', 'Xenova/yolov5s')
    } catch (e) {
      console.warn('Failed to load object detection model:', e)
    }

    try {
      this.captioner = await pipeline('image-to-text', 'Xenova/blip-image-captioning-base')
    } catch (e) {
      console.warn('Failed to load image captioning model:', e)
    }
  }

  async classifyImage(imageData: string): Promise<ImageClassificationResult> {
    if (!this.classifier) {
      await this.ensureClassifierLoaded()
    }

    if (!this.classifier) {
      return {
        labels: [
          { label: '无法识别', score: 0.5 },
          { label: '未知物体', score: 0.3 },
          { label: '图片', score: 0.2 }
        ]
      }
    }

    try {
      const result = await this.classifier(imageData)
      
      const labels: Array<{ label: string; score: number }> = result.map((item: any) => ({
        label: item.label,
        score: Math.round(item.score * 100) / 100
      }))

      this.saveAnalysis('classification', { labels })

      return { labels }
    } catch (error) {
      console.error('Image classification error:', error)
      return this.getFallbackClassification()
    }
  }

  private async ensureClassifierLoaded() {
    if (!this.classifier && !this.isLoading) {
      try {
        this.classifier = await pipeline('image-classification', 'Xenova/resnet-50')
      } catch (e) {
        console.warn('Failed to load classifier:', e)
      }
    }
  }

  private getFallbackClassification(): ImageClassificationResult {
    return {
      labels: [
        { label: '无法识别', score: 0.5 },
        { label: '未知物体', score: 0.3 },
        { label: '图片', score: 0.2 }
      ]
    }
  }

  async detectObjects(imageData: string): Promise<ObjectDetectionResult> {
    if (!this.detector) {
      await this.ensureDetectorLoaded()
    }

    if (!this.detector) {
      return { boxes: [] }
    }

    try {
      const result = await this.detector(imageData)
      
      const boxes: ObjectDetectionResult['boxes'] = result.map((item: any) => ({
        x1: item.box.xmin,
        y1: item.box.ymin,
        x2: item.box.xmax,
        y2: item.box.ymax,
        label: item.label,
        score: Math.round(item.score * 100) / 100
      }))

      this.saveAnalysis('object-detection', { boxes })

      return { boxes }
    } catch (error) {
      console.error('Object detection error:', error)
      return { boxes: [] }
    }
  }

  private async ensureDetectorLoaded() {
    if (!this.detector && !this.isLoading) {
      try {
        this.detector = await pipeline('object-detection', 'Xenova/yolov5s')
      } catch (e) {
        console.warn('Failed to load detector:', e)
      }
    }
  }

  async generateCaption(imageData: string): Promise<ImageCaptionResult> {
    if (!this.captioner) {
      await this.ensureCaptionerLoaded()
    }

    if (!this.captioner) {
      return { caption: '无法生成图片描述' }
    }

    try {
      const result = await this.captioner(imageData)
      const caption = result[0]?.generated_text || '无法生成图片描述'

      this.saveAnalysis('captioning', { caption })

      return { caption }
    } catch (error) {
      console.error('Image captioning error:', error)
      return { caption: '无法生成图片描述' }
    }
  }

  private async ensureCaptionerLoaded() {
    if (!this.captioner && !this.isLoading) {
      try {
        this.captioner = await pipeline('image-to-text', 'Xenova/blip-image-captioning-base')
      } catch (e) {
        console.warn('Failed to load captioner:', e)
      }
    }
  }

  async detectFaces(imageData: string): Promise<FaceDetectionResult> {
    if (!this.detector) {
      await this.ensureDetectorLoaded()
    }

    if (!this.detector) {
      return { faces: [] }
    }

    try {
      const result = await this.detector(imageData)
      
      const faces: FaceDetectionResult['faces'] = result
        .filter((item: any) => item.label === 'person' || item.label === 'face')
        .map((item: any) => ({
          x1: item.box.xmin,
          y1: item.box.ymin,
          x2: item.box.xmax,
          y2: item.box.ymax,
          confidence: Math.round(item.score * 100) / 100
        }))

      this.saveAnalysis('face-detection', { faces })

      return { faces }
    } catch (error) {
      console.error('Face detection error:', error)
      return { faces: [] }
    }
  }

  async analyzeImage(imageData: string): Promise<{
    classification?: ImageClassificationResult
    objects?: ObjectDetectionResult
    caption?: ImageCaptionResult
    faces?: FaceDetectionResult
  }> {
    const [classification, objects, caption, faces] = await Promise.all([
      this.classifyImage(imageData),
      this.detectObjects(imageData),
      this.generateCaption(imageData),
      this.detectFaces(imageData)
    ])

    return { classification, objects, caption, faces }
  }

  async getImageInfo(imageData: string): Promise<{
    width: number
    height: number
    format: string
    size: number
    hasObjects: boolean
    objectCount: number
    hasPeople: boolean
    peopleCount: number
  }> {
    let width = 0
    let height = 0
    let format = 'unknown'
    let size = imageData.length

    try {
      const img = new Image()
      await new Promise((resolve, reject) => {
        img.onload = () => {
          width = img.width
          height = img.height
          resolve(true)
        }
        img.onerror = reject
        img.src = imageData
      })

      if (imageData.startsWith('data:image/jpeg')) {
        format = 'JPEG'
      } else if (imageData.startsWith('data:image/png')) {
        format = 'PNG'
      } else if (imageData.startsWith('data:image/webp')) {
        format = 'WebP'
      } else if (imageData.startsWith('data:image/gif')) {
        format = 'GIF'
      }
    } catch (e) {
      console.warn('Failed to get image dimensions:', e)
    }

    const objects = await this.detectObjects(imageData)
    const hasObjects = objects.boxes.length > 0
    const objectCount = objects.boxes.length

    const faces = await this.detectFaces(imageData)
    const hasPeople = faces.faces.length > 0
    const peopleCount = faces.faces.length

    return {
      width,
      height,
      format,
      size,
      hasObjects,
      objectCount,
      hasPeople,
      peopleCount
    }
  }

  async isSafeImage(imageData: string): Promise<{
    safe: boolean
    reason: string
    confidence: number
  }> {
    const classification = await this.classifyImage(imageData)
    const objects = await this.detectObjects(imageData)

    const unsafeKeywords = ['gun', 'weapon', 'knife', 'fire', 'explosion', 'blood', 'violence']
    const adultKeywords = ['person', 'face']

    let safe = true
    let reason = '图片内容安全'
    let confidence = 0.9

    for (const label of classification.labels) {
      const lowerLabel = label.label.toLowerCase()
      if (unsafeKeywords.some(kw => lowerLabel.includes(kw))) {
        safe = false
        reason = `检测到不安全内容: ${label.label}`
        confidence = label.score
        break
      }
    }

    if (safe) {
      const personCount = objects.boxes.filter(b => b.label === 'person').length
      if (personCount > 0 && personCount <= 2) {
        safe = true
      } else if (personCount > 5) {
        safe = false
        reason = '图片中人物过多，可能存在安全风险'
        confidence = 0.7
      }
    }

    return { safe, reason, confidence }
  }

  private saveAnalysis(task: string, result: any) {
    const imageId = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    this.analysisHistory.set(imageId, {
      imageId,
      task,
      result,
      confidence: 0.85,
      timestamp: Date.now()
    })
  }

  getModelStatus(): {
    classifier: boolean
    detector: boolean
    captioner: boolean
    faceDetector: boolean
    isLoading: boolean
  } {
    return {
      classifier: !!this.classifier,
      detector: !!this.detector,
      captioner: !!this.captioner,
      faceDetector: !!this.faceDetector,
      isLoading: this.isLoading
    }
  }

  getStats() {
    return {
      totalAnalyses: this.analysisHistory.size,
      modelStatus: this.getModelStatus()
    }
  }
}

export const aiImageAnalysis = new AIImageAnalysis()